import { type KeyboardEvent, type PointerEvent, useRef } from "react";
import { cn } from "@/ui/kit/cn";

export function GradientSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  gradient,
  format = (n) => n.toFixed(2),
  className,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  gradient?: string;
  format?: (n: number) => string;
  className?: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const pct = ((value - min) / (max - min)) * 100;

  const fromPointer = (e: PointerEvent) => {
    const box = track.current?.getBoundingClientRect();
    if (!box) return;
    const t = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    onChange(Math.round((min + t * (max - min)) / step) * step);
  };

  const onKey = (e: KeyboardEvent) => {
    const big = e.shiftKey ? 10 : 1;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") onChange(Math.min(max, value + step * big));
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown")
      onChange(Math.max(min, value - step * big));
    else if (e.key === "Home") onChange(min);
    else if (e.key === "End") onChange(max);
    else return;
    e.preventDefault();
  };

  return (
    <div className={cn("grid grid-cols-[2.2rem_1fr_3.2rem] items-center gap-2", className)}>
      <span className="text-muted text-xs">{label}</span>
      <div
        ref={track}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={format(value)}
        className="relative h-3.5 cursor-pointer touch-none rounded-full ring-1 ring-black/30"
        style={{ background: gradient ?? "var(--chrome-raised)" }}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          fromPointer(e);
        }}
        onPointerMove={(e) => e.buttons === 1 && fromPointer(e)}
        onKeyDown={onKey}
      >
        <span
          className="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,.5),0_1px_3px_rgba(0,0,0,.4)]"
          style={{ left: `${pct}%` }}
        />
      </div>
      <span className="text-right font-mono text-[11px] text-muted tabular-nums">
        {format(value)}
      </span>
    </div>
  );
}
